import { Fragment, useMemo } from 'react';

import {
  layoutGraphMiniature,
  layoutMindmapNative,
} from '../../../mindmap/mindmap-native';
import type { MindmapData } from '../../../mindmap/mindmap-types';
import { View } from '../../primitives/View';
import { BRAND_HEX, mirrorShade } from '../../styles/brand-colors';
import { Path, Rect, Svg, TextSvg } from '../../svg';
import { useTheme } from '../../theme';

/**
 * A mind map at tile size: its shape, not its words.
 *
 * Drawn from the SAME layout the real canvas uses (`layoutMindmapNative`
 * adapts the one shared tidy tree), so the miniature cannot drift from the
 * map it stands for — and drawn from the logical tree rather than a saved
 * geometry, because a freshly generated map is stored without one.
 *
 * No labels. A thirty-node tree fitted into a 200px frame puts type at about
 * two pixels, which is a smudge that says less than the branch shape does.
 * What reads at this size is the silhouette: a filled root with coloured
 * branches spreading from it.
 */
export function MindmapMiniature({
  mindmap,
  graph,
  width,
  height,
}: {
  mindmap: MindmapData;
  /** The sheet as the canvas holds it. Present = drawn as ARRANGED (its own
   *  positions, loose nodes, zones), fitted whole — see GraphMiniature. */
  graph?: Parameters<typeof layoutGraphMiniature>[0];
  /** The frame to fit into, in points. */
  width: number;
  height: number;
}) {
  const { isDark } = useTheme();
  if (graph && graph.nodes.length > 0)
    return <GraphMiniature graph={graph} width={width} height={height} isDark={isDark} />;
  return (
    <TreeMiniature mindmap={mindmap} width={width} height={height} isDark={isDark} />
  );
}
(MindmapMiniature as { displayName?: string }).displayName = 'MindmapMiniature';

/** No sheet is drawn bigger than this: one node must not fill the tile. */
const MAX_SCALE = 0.5;
/** How far a sheet may be cropped to stay readable: this share of "fill". */
const CROP = 0.6;
/** Labels show only where they are legible (canvas px × scale). */
const MIN_LABEL_PX = 6.5;

/**
 * A canvas sheet as laid out, centred on its main node. Zoomed to read: a
 * sheet that fits is shown whole; a wide one is cropped rather than shrunk to
 * an unreadable strip (fitting a two-sided map into a portrait tile does
 * that), and nothing is ever enlarged past MAX_SCALE, so a sparse sheet reads
 * as a few nodes, not one block. Nodes are labelled wherever the type is
 * legible, so two sheets of the same deck tell apart at a glance.
 */
function GraphMiniature({
  graph,
  width,
  height,
  isDark,
}: {
  graph: Parameters<typeof layoutGraphMiniature>[0];
  width: number;
  height: number;
  isDark: boolean;
}) {
  const layout = useMemo(() => layoutGraphMiniature(graph, { isDark }), [graph, isDark]);
  if (layout.width <= 0 || layout.height <= 0) return null;
  const fit = Math.min(width / layout.width, height / layout.height);
  const fill = Math.max(width / layout.width, height / layout.height);
  const scale = Math.min(MAX_SCALE, Math.max(fit, fill * CROP));
  // Centre on the main node (the first, if several), else the sheet's middle;
  // a sheet that fits is centred whole.
  const main = layout.nodes.find((n) => n.important);
  const fits = layout.width * scale <= width && layout.height * scale <= height;
  const cx = !fits && main ? main.x + main.width / 2 : layout.minX + layout.width / 2;
  const cy = !fits && main ? main.y + main.height / 2 : layout.minY + layout.height / 2;
  const offsetX = width / 2 - (cx - layout.minX) * scale;
  const offsetY = height / 2 - (cy - layout.minY) * scale;
  // Label ink from the palette (mirrored in dark), white on a filled node.
  const labelOnFill = BRAND_HEX.secondary['50'];
  const labelInk = BRAND_HEX.secondary[isDark ? mirrorShade('800') : '800'];
  const vx = layout.minX - offsetX / scale;
  const vy = layout.minY - offsetY / scale;
  return (
    <View className="absolute inset-0 overflow-hidden">
      <Svg
        width={width}
        height={height}
        viewBox={`${vx} ${vy} ${width / scale} ${height / scale}`}>
        {/* Zones: see-through areas with a dashed edge and their name — never
            mistaken for a (solid, outlined) node. */}
        {layout.zones.map((z) => (
          <Fragment key={z.id}>
            <Rect
              x={z.x}
              y={z.y}
              width={z.width}
              height={z.height}
              rx={28}
              fill={z.fill}
              fillOpacity={0.55}
              stroke={z.stroke}
              strokeOpacity={0.8}
              strokeWidth={2}
              strokeDasharray="10 8"
            />
            {z.name && 14 * scale >= MIN_LABEL_PX ? (
              <TextSvg
                x={z.x + 18}
                y={z.y + 22}
                fontSize={14}
                fontWeight={700}
                dominantBaseline="central"
                fill={z.ink}>
                {z.name}
              </TextSvg>
            ) : null}
          </Fragment>
        ))}
        {layout.edges.map((edge) => (
          <Path
            key={edge.id}
            d={edge.d}
            stroke={edge.stroke}
            strokeWidth={3}
            fill="none"
          />
        ))}
        {layout.nodes.map((node) => {
          const fontPx = node.important ? 16 : 13;
          const showLabel = fontPx * scale >= MIN_LABEL_PX;
          const maxChars = Math.max(3, Math.floor((node.width - 20) / (fontPx * 0.56)));
          const text =
            node.label.length > maxChars
              ? `${node.label.slice(0, maxChars - 1)}…`
              : node.label;
          return (
            <Fragment key={node.id}>
              <Path
                d={roundedRectPath(node.x, node.y, node.width, node.height, 10)}
                fill={node.colors.background}
                stroke={node.colors.border}
                strokeWidth={2}
              />
              {showLabel ? (
                <TextSvg
                  x={node.x + node.width / 2}
                  y={node.y + node.height / 2}
                  fontSize={fontPx}
                  fontWeight={node.important ? 700 : 500}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill={node.colors.filled ? labelOnFill : labelInk}>
                  {text}
                </TextSvg>
              ) : null}
            </Fragment>
          );
        })}
      </Svg>
    </View>
  );
}

/** A map from before the canvas: the shared tidy tree's silhouette. */
function TreeMiniature({
  mindmap,
  width,
  height,
  isDark,
}: {
  mindmap: MindmapData;
  width: number;
  height: number;
  isDark: boolean;
}) {
  const layout = useMemo(
    () => layoutMindmapNative(mindmap, { isDark, padding: 8 }),
    [mindmap, isDark],
  );

  if (layout.nodes.length === 0 || layout.width <= 0 || layout.height <= 0) return null;

  // Fill the frame rather than fitting inside it: a two-sided tree is much
  // wider than it is tall, and fitting would leave a thin stripe adrift in a
  // portrait tile. Overflow is clipped by the frame, which crops towards the
  // root — the part worth showing.
  const scale = Math.max(width / layout.width, height / layout.height);
  const offsetX = (width - layout.width * scale) / 2;
  const offsetY = (height - layout.height * scale) / 2;

  return (
    <View className="absolute inset-0 overflow-hidden">
      <Svg
        width={width}
        height={height}
        viewBox={`${-offsetX / scale} ${-offsetY / scale} ${width / scale} ${height / scale}`}>
        {layout.edges.map((edge) => (
          <Path
            key={edge.id}
            d={edge.d}
            stroke={edge.stroke}
            strokeWidth={2}
            fill="none"
          />
        ))}
        {layout.nodes.map((node) => (
          <Path
            key={node.id}
            // A rounded rect as a path, so the whole miniature is one
            // primitive set and renders identically on both platforms.
            d={roundedRectPath(node.x, node.y, node.width, node.height, 8)}
            fill={node.colors.background}
            stroke={node.colors.border}
            strokeWidth={1.5}
          />
        ))}
      </Svg>
    </View>
  );
}

function roundedRectPath(x: number, y: number, w: number, h: number, r: number): string {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  return [
    `M${x + radius},${y}`,
    `H${x + w - radius}`,
    `A${radius},${radius} 0 0 1 ${x + w},${y + radius}`,
    `V${y + h - radius}`,
    `A${radius},${radius} 0 0 1 ${x + w - radius},${y + h}`,
    `H${x + radius}`,
    `A${radius},${radius} 0 0 1 ${x},${y + h - radius}`,
    `V${y + radius}`,
    `A${radius},${radius} 0 0 1 ${x + radius},${y}`,
    'Z',
  ].join(' ');
}
